import React, { useState, useRef, useEffect } from 'react';
import { generateContent } from '@/services/aiService';
import { Copy, Bot, User, Send, Sparkles } from 'lucide-react';
import Adminsidebar from './Sidebar';
import Adminheader from './Header';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SidebarProvider } from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

function ErrorBoundary({ children }: ErrorBoundaryProps) {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleError = () => setHasError(true);
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return <p className="text-sm text-destructive font-medium p-4">Error loading component. Please refresh.</p>;
  }

  return <>{children}</>;
}

function ChatbotPage() {
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState<{ sender: string; text: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [schoolID, setSchoolID] = useState<string | null>(null); // Added state
  
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Fetch School ID for AI Pause Check
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;
          const affiliations = (claims.affiliations as any[]) || [];
          const defaultAff = claims.defaultAffiliation as any;
          
          let sid = defaultAff?.schoolID;
          if (!sid) {
             const found = affiliations.find((aff: any) => aff.schoolID);
             sid = found?.schoolID;
          }
          if (!sid) sid = claims.schoolID as string;
          
          setSchoolID(sid || null);
        } catch (e) {
          console.error("Error fetching user claims:", e);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = async () => {
    if (!chatInput.trim()) return;
    
    const currentInput = chatInput;
    setChatInput(""); // Clear immediately for better UX
    
    // Reset height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    setIsLoading(true);
    const userMessage = { sender: "You", text: currentInput };
    setChatHistory((prev) => [...prev, userMessage]);

    try {
      // Pass schoolID to generateContent (checks permissions)
      const response = await generateContent(currentInput, schoolID || undefined);
      setChatHistory((prev) => [...prev, { sender: "AI", text: response }]);
    } catch (error: any) {
      const errorMessage = error.message || "Error: Could not generate response. Please try again.";
      setChatHistory((prev) => [
        ...prev,
        { sender: "AI", text: errorMessage },
      ]);
      console.error("Chat Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chatHistory, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [chatInput]);

  return (
    <ErrorBoundary>
      <SidebarProvider defaultOpen={true}>
        <div className="flex h-[100dvh] w-full bg-gray-50 dark:bg-gray-900 font-sans overflow-hidden">
          {/* Sidebar: hidden on mobile, fixed on desktop */}
          <div className="hidden md:block fixed top-0 left-0 h-full w-64 z-50 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
            <Adminsidebar onSectionChange={() => {}} />
          </div>

          {/* Main Chat Section */}
          <div className="flex flex-col flex-1 h-full md:ml-64 relative w-full bg-white dark:bg-[#020817]">
            {/* Header */}
            <div className="sticky top-0 z-40 w-full flex-none bg-white/80 dark:bg-[#020817]/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
              <Adminheader />
            </div>
            
            {/* Chat Content Container */}
            <div className="flex flex-col flex-1 min-h-0 relative">
              
              {/* Page Title Area */}
              <div className="flex-none px-4 py-3 sm:px-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-[#020817]">
                <div className="flex items-center gap-2">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                      AI Assistant
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Powered by Gemini AI
                    </p>
                  </div>
                </div>
              </div>

              {/* Toast Notification */}
              {showToast && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-4 py-2 rounded-full shadow-lg text-sm font-medium animate-in fade-in slide-in-from-top-2 duration-200">
                  Copied to clipboard
                </div>
              )}

              {/* Messages Area */}
              <div
                ref={chatContainerRef}
                className="flex flex-col flex-1 overflow-y-auto p-4 sm:p-6 pb-4 gap-6 scroll-smooth"
              >
                {chatHistory.length === 0 && !isLoading && (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8 opacity-0 animate-in fade-in duration-500">
                    <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center mb-4">
                      <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      How can I help you today?
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-md text-sm">
                      Ask me anything about your data, generate content, or get assistance with your tasks.
                    </p>
                  </div>
                )}

                {chatHistory.map((message, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex w-full gap-3 sm:gap-4 group",
                      message.sender === "You" ? "justify-end" : "justify-start"
                    )}
                  >
                    {/* AI Avatar */}
                    {message.sender !== "You" && (
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-gray-700 flex-none">
                        <AvatarImage src="/bot-avatar.png" />
                        <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                          <Bot className="h-5 w-5" />
                        </AvatarFallback>
                      </Avatar>
                    )}

                    {/* Message Bubble */}
                    <div className={cn(
                      "flex flex-col max-w-[85%] sm:max-w-[75%]",
                      message.sender === "You" ? "items-end" : "items-start"
                    )}>
                      <div className="flex items-end gap-2">
                        <div
                          className={cn(
                            "px-4 py-3 rounded-2xl text-sm sm:text-base shadow-sm whitespace-pre-wrap break-words",
                            message.sender === "You"
                              ? "bg-blue-600 text-white rounded-br-none"
                              : "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-none"
                          )}
                        >
                          {message.text}
                        </div>
                        
                        {/* Copy Button for AI */}
                        {message.sender === "AI" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              "h-8 w-8 rounded-full opacity-0 transition-opacity duration-200",
                              "group-hover:opacity-100 focus:opacity-100",
                              // Always visible on touch devices if needed, but hover works well enough usually
                              "md:opacity-0" 
                            )}
                            onClick={() => handleCopy(message.text)}
                            title="Copy response"
                          >
                            <Copy className="h-4 w-4 text-gray-500" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* User Avatar */}
                    {message.sender === "You" && (
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-gray-700 flex-none">
                        <AvatarImage src="/user-avatar.png" />
                        <AvatarFallback className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                          <User className="h-5 w-5" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}

                {/* Typing Indicator */}
                {isLoading && (
                  <div className="flex w-full gap-3 sm:gap-4">
                    <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-gray-200 dark:border-gray-700 flex-none">
                      <AvatarFallback className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                        <Bot className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm flex items-center gap-1">
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                      <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></span>
                    </div>
                  </div>
                )}
                
                {/* Spacer behind floating input */}
                <div ref={bottomRef} className="h-40 flex-none" />
              </div>

              {/* Input Area */}
              <div className="absolute bottom-0 left-0 w-full z-20 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-[#020817] dark:via-[#020817]/95 dark:to-transparent pt-10 pb-6 px-4">
                <div className="max-w-2xl mx-auto">
                  <div className="relative flex items-center gap-2 bg-gray-100 dark:bg-gray-800/80 border border-transparent dark:border-gray-700/50 rounded-3xl p-1.5 px-3 shadow-sm focus-within:ring-1 focus-within:ring-white/20 transition-all">
                    <Textarea
                      ref={textareaRef}
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Ask anything..."
                      className="min-h-[40px] max-h-[160px] w-full resize-none bg-transparent border-none focus-visible:ring-0 px-2 py-2 text-sm shadow-none placeholder:text-gray-500"
                      disabled={isLoading}
                      rows={1}
                    />
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading || !chatInput.trim()}
                      size="icon"
                      className={cn(
                        "h-8 w-8 rounded-full shrink-0 transition-all duration-200",
                        chatInput.trim() 
                          ? "bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 shadow-sm" 
                          : "bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed"
                      )}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-center text-xs text-muted-foreground mt-3 font-medium opacity-70">
                    AI can make mistakes. Please verify important information.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    </ErrorBoundary>
  );

}

export default ChatbotPage;